import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  IconButton,
  Divider,
  Alert
} from '@mui/material'
import {
  Close,
  ShoppingCart,
  Business,
  CalendarToday,
  Description,
  AttachMoney,
  PictureAsPdf,
  Repeat,
  CheckCircle,
  Schedule,
  Warning
} from '@mui/icons-material'

import { formatCurrency, formatDate } from '../../utils/formatters'
import useAuthStore from '../../store/authStore'
import RoleGuard from '../../components/Auth/RoleGuard'
import { useUpdatePurchaseOrderStatus, useDeletePurchaseOrder } from '../../hooks/usePurchaseOrders'
import { purchaseOrdersAPI } from '../../utils/api'

const PurchaseOrderDetail = ({ open, onClose, purchaseOrder }) => {
  const { isAdmin } = useAuthStore()
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false)
  const [currentPdfUrl, setCurrentPdfUrl] = useState(null)
  
  const updateStatusMutation = useUpdatePurchaseOrderStatus()
  const deletePurchaseOrderMutation = useDeletePurchaseOrder()

  const getStatusColor = (status) => {
    switch (status) {
      case 'POR_FACTURAR': return 'warning'
      case 'FACTURADA': return 'success'
      default: return 'default'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'POR_FACTURAR': return <Schedule fontSize="small" />
      case 'FACTURADA': return <CheckCircle fontSize="small" />
      default: return <ShoppingCart fontSize="small" />
    }
  }

  const getTypeIcon = (type) => {
    return type === 'PERIODICA' ? <Repeat fontSize="small" /> : <ShoppingCart fontSize="small" />
  }

  const getTypeColor = (type) => {
    return type === 'PERIODICA' ? 'primary' : 'default'
  }

  const handleViewPdf = async () => {
    if (purchaseOrder?.pdfKey) {
      try {
        const response = await purchaseOrdersAPI.getPdfUrl(purchaseOrder.id)
        if (response.signedUrl) {
          setCurrentPdfUrl(response.signedUrl)
          setPdfViewerOpen(true)
        }
      } catch (error) {
        console.error('Error getting PDF URL:', error)
      }
    }
  }

  const handleStatusChange = async (newStatus) => {
    try {
      await updateStatusMutation.mutateAsync({
        id: purchaseOrder.id,
        status: newStatus
      })
      // Close dialog after successful update
      onClose()
    } catch (error) {
      console.error('Error updating status:', error)
    }
  }

  const handleDelete = async () => {
    if (window.confirm('¿Estás seguro de que deseas eliminar esta orden de compra?')) {
      try {
        await deletePurchaseOrderMutation.mutateAsync(purchaseOrder.id)
        onClose()
      } catch (error) {
        console.error('Error deleting purchase order:', error)
      }
    }
  }

  if (!purchaseOrder) return null

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCart color="primary" />
              <Box>
                <Typography variant="h6">
                  Orden de Compra {purchaseOrder.ocNumber}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Detalles de la orden de compra
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={3}>
            {/* Left Column - Basic Info */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, height: 'fit-content' }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Business />
                  Información General
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Número de OC
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {purchaseOrder.ocNumber}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Cliente
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {purchaseOrder.client?.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {purchaseOrder.client?.rut}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Fecha de Emisión
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formatDate(purchaseOrder.issueDate)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Tipo de OC
                    </Typography>
                    <Chip
                      label={purchaseOrder.ocType === 'PERIODICA' ? 'Periódica' : 'Única'}
                      color={getTypeColor(purchaseOrder.ocType)}
                      size="small"
                      variant="outlined"
                      icon={getTypeIcon(purchaseOrder.ocType)}
                      sx={{ mb: 1 }}
                    />
                    {purchaseOrder.isPeriodic && purchaseOrder.billingDay && (
                      <Typography variant="body2" color="text.secondary" display="block">
                        Facturación: Día {purchaseOrder.billingDay} de cada mes
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Estado
                    </Typography>
                    <Chip
                      label={purchaseOrder.status === 'POR_FACTURAR' ? 'Por Facturar' : 'Facturada'}
                      color={getStatusColor(purchaseOrder.status)}
                      size="small"
                      variant="outlined"
                      icon={getStatusIcon(purchaseOrder.status)}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Right Column - Financial Info */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, height: 'fit-content' }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AttachMoney />
                  Información Financiera
                </Typography>
                
                <Grid container spacing={2}>
                  {/* UF Information */}
                  {purchaseOrder.netAmountUF && purchaseOrder.ufValue && (
                    <>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Monto Neto en UF
                        </Typography>
                        <Typography variant="h6" fontWeight={600}>
                          {purchaseOrder.netAmountUF} UF
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Valor UF del día
                        </Typography>
                        <Typography variant="h6" fontWeight={600}>
                          {formatCurrency(purchaseOrder.ufValue)}
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Monto Neto
                    </Typography>
                    <Typography variant="h6" fontWeight={600}>
                      {formatCurrency(purchaseOrder.netAmount)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      IVA ({purchaseOrder.ivaPercentage}%)
                    </Typography>
                    <Typography variant="h6" fontWeight={600}>
                      {formatCurrency(purchaseOrder.ivaAmount)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Total
                    </Typography>
                    <Typography variant="h5" fontWeight={600} color="primary.main">
                      {formatCurrency(purchaseOrder.totalAmount)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Description />
                  Descripción
                </Typography>
                <Typography variant="body1">
                  {purchaseOrder.description}
                </Typography>
              </Paper>
            </Grid>

            {/* PDF Section */}
            {purchaseOrder.pdfKey && (
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PictureAsPdf />
                    Documento PDF
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<PictureAsPdf />}
                    onClick={handleViewPdf}
                  >
                    Ver PDF
                  </Button>
                </Paper>
              </Grid>
            )}

            {/* Periodic Info Alert */}
            {purchaseOrder.isPeriodic && (
              <Grid item xs={12}>
                <Alert 
                  severity="info" 
                  icon={<Repeat />}
                  sx={{ mb: 2 }}
                >
                  <Typography variant="body2">
                    <strong>Orden de Compra Periódica</strong>
                  </Typography>
                  <Typography variant="caption" display="block">
                    Esta orden de compra generará facturas automáticamente cada mes en el día {purchaseOrder.billingDay}.
                  </Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={onClose}
            color="inherit"
          >
            Cerrar
          </Button>
          
          <RoleGuard requireAdmin fallback={null}>
            {/* Status change buttons */}
            {purchaseOrder.status !== 'POR_FACTURAR' && (
              <Button
                variant="outlined"
                color="warning"
                startIcon={<Schedule />}
                onClick={() => handleStatusChange('POR_FACTURAR')}
                disabled={updateStatusMutation.isLoading}
              >
                Marcar como Por Facturar
              </Button>
            )}

            {purchaseOrder.status !== 'FACTURADA' && (
              <Button
                variant="outlined"
                color="success"
                startIcon={<CheckCircle />}
                onClick={() => handleStatusChange('FACTURADA')}
                disabled={updateStatusMutation.isLoading}
              >
                Marcar como Facturada
              </Button>
            )}

            <Button
              variant="contained"
              color="error"
              onClick={handleDelete}
              disabled={deletePurchaseOrderMutation.isLoading}
            >
              {deletePurchaseOrderMutation.isLoading ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </RoleGuard>
        </DialogActions>
      </Dialog>

      {/* PDF Viewer Modal */}
      {pdfViewerOpen && (
        <Dialog
          open={pdfViewerOpen}
          onClose={() => setPdfViewerOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: { height: '90vh' }
          }}
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6">
                PDF - Orden de Compra {purchaseOrder.ocNumber}
              </Typography>
              <IconButton onClick={() => setPdfViewerOpen(false)}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            {currentPdfUrl && (
              <iframe
                src={currentPdfUrl}
                width="100%"
                height="100%"
                style={{ border: 'none', minHeight: '70vh' }}
                title={`PDF - ${purchaseOrder.ocNumber}`}
              />
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}

export default PurchaseOrderDetail
